// ADR 25.9.26g §3 — the one reader of the install affordance.
//
// Moved out of `InstallEntry.tsx` so the account menu and the install strip
// read the same inputs in the same way (23.9.26t: one reader per signal). The
// decision stays `installAffordance`'s; this only gathers its inputs, and
// re-renders when a `beforeinstallprompt` is captured or spent, or when the
// installed-elsewhere probe answers (25.9.26h §4).
//
// Since 25.9.26h §3 it also returns the platform, typed so that an
// instruction affordance always carries a `StepsPlatform`: `InstallSteps` can
// never be handed `'other'`. Since 6.10.26a §4 it returns the iOS browser too,
// for the emitters' `ios_browser`; and `useInstallRequest` reads §5's
// `?install=1` request from the same store. Since 6.10.26b §2 a `handoff`
// state carries its target, so the panel is never drawn without one. Since
// 16.10.26b §3 the iOS arm is its own (`platform: 'ios'`), routed to the guide
// modal, and the other instruction arms carry a `StepsPlatform` without `ios`.
// Since 16.10.26f §5 Mac Safari's arm is its own too (`platform:
// 'mac_safari'`), routed to the install guide, so `menu_instructions` carries
// a `StepsPlatform` that is neither.

import { useSyncExternalStore } from 'react'

import { displayModeOf } from './displayMode'
import { handoffTargetOf, type HandoffTarget } from './handoff'
import { installAffordance, iosBrowserOf, isInAppBrowser, platformOf, type IosBrowser, type Platform } from './installAffordance'
import {
  installedElsewhere,
  installRequest,
  promptAvailable,
  subscribeInstallPrompt,
  type InstallRequest,
} from './installPrompt'
import type { StepsPlatform } from './installStepsTable'

export type InstallState = { iosBrowser: IosBrowser } & (
  | { affordance: 'none'; platform: Platform }
  | { affordance: 'prompt'; platform: Platform }
  | { affordance: 'ios_instructions'; platform: 'ios' }
  | { affordance: 'mac_instructions'; platform: 'mac_safari' }
  | { affordance: 'menu_instructions'; platform: StepsPlatform }
  | { affordance: 'handoff'; platform: 'other'; target: HandoffTarget }
)

export function useInstallAffordance(): InstallState {
  const canPrompt = useSyncExternalStore(subscribeInstallPrompt, promptAvailable)
  const elsewhere = useSyncExternalStore(subscribeInstallPrompt, installedElsewhere)
  const userAgent = navigator.userAgent
  const platform = platformOf(userAgent, navigator.maxTouchPoints ?? 0)
  const iosBrowser = iosBrowserOf(userAgent)
  const handoffTarget = platform === 'other' ? handoffTargetOf(userAgent) : null
  const affordance = installAffordance({
    platform,
    standalone: displayModeOf(window) === 'standalone',
    promptAvailable: canPrompt,
    inAppBrowser: isInAppBrowser(userAgent),
    installedElsewhere: elsewhere,
    handoffTarget,
  })
  // `installAffordance` answers an instruction only for a platform other than
  // `other`, and `handoff` only with a target; the checks restate that for the
  // type system.
  if (affordance === 'prompt') return { affordance, platform, iosBrowser }
  if (affordance === 'handoff' && handoffTarget !== null) {
    return { affordance, platform: 'other', target: handoffTarget, iosBrowser }
  }
  if (affordance === 'none' || affordance === 'handoff' || platform === 'other') {
    return { affordance: 'none', platform, iosBrowser }
  }
  return instructionState(affordance, platform, iosBrowser)
}

/**
 * 16.10.26b §3 — `ios_instructions` is answered only for `ios`, and the other
 * instructions never for it; 16.10.26f §5 — likewise `mac_instructions` only
 * for `mac_safari`, and `menu_instructions` never for it. TypeScript does not
 * narrow `platform` from `affordance`, so the pairing is restated; a mismatch
 * answers `none`.
 */
function instructionState(
  affordance: 'ios_instructions' | 'mac_instructions' | 'menu_instructions',
  platform: Exclude<Platform, 'other'>,
  iosBrowser: IosBrowser,
): InstallState {
  if (affordance === 'ios_instructions' && platform === 'ios') return { affordance, platform, iosBrowser }
  if (affordance === 'mac_instructions' && platform === 'mac_safari') return { affordance, platform, iosBrowser }
  if (affordance === 'menu_instructions' && platform !== 'ios' && platform !== 'mac_safari') {
    return { affordance, platform, iosBrowser }
  }
  return { affordance: 'none', platform, iosBrowser }
}

/** ADR 6.10.26a §5 — the `?install=1` request, `none` / `pending` / `open`. */
export function useInstallRequest(): InstallRequest {
  return useSyncExternalStore(subscribeInstallPrompt, installRequest)
}
